import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Main.css";
import BlueTracIconUrl from "./icons/Group 62.svg";
import RedTracIconUrl from "./icons/Group 63.svg";
import MapIconUrl from "./icons/Icon awesome-map-marked.svg";
import ContactIconUrl from "./icons/Group 39.svg";
import okIconUrl from "./icons/Validation - check.svg";
import errorIconUrl from "./icons/Icon awesome-exclamation-triangle.svg";
import { Container, Row, Col, Table, Stack, Image } from "react-bootstrap";

// Define base API URL for easier switching between environments
//const BASE_API_URL = "http://localhost:8000"; // For local development
const BASE_API_URL = "https://api.platform.realtimesprayingpalaiopanagia.gr"; // For production

const Map = () => {
  const [tractorData, setTractorData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [popupContent, setPopupContent] = useState();
  const [selectedTractorId, setSelectedTractorId] = useState(null); // Track the selected tractor ID
  const [sprayer, setSprayer] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sprayingData, setSprayingData] = useState([]);
  const [nonSprayingData, setNonSprayingData] = useState([]);

  // Alarm threshold constants - Easy to modify
  const LEAK_THRESHOLD = 0.001;
  const WIND_SPEED_THRESHOLD = 4;
  const HUMIDITY_THRESHOLD = 30; // Alarm when BELOW this value
  const TEMPERATURE_THRESHOLD = 28;
  const TIME_THRESHOLD = 5; // Minutes

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const handleMarkerClick = (tractor) => {
    setPopupContent(tractor);
    setSelectedTractorId(tractor.device_id); // Store the selected tractor ID
    setShowTable(true); // Show table immediately when marker is clicked
  };

  useEffect(() => {
    if (popupContent) {
      setSprayingData(popupContent.historical_data_spray || []);
      setNonSprayingData(popupContent.historical_data_nonspray || []);
      
      const getSprayer = async () => {
        try {
          const response = await fetch(
            `${BASE_API_URL}/api/getsprayer/`,
            {
              method: "GET",
              credentials: "include",
              headers: new Headers({
                Accept: "application/json",
                "Content-Type": "application/json",
                "device-id": popupContent.device_id,
              }),
            }
          );
          const data = await response.json();
          setSprayer(data.user);
        } catch (error) {
          if (error.message === "Failed to fetch active user") {
            setErrorMessage("An error occurred while fetching active user");
          } else {
            setErrorMessage("No active user found for the given device ID");
          }
        }
      };
      getSprayer();
    }
  }, [popupContent]);

  useEffect(() => {
    const apiUrl = `${BASE_API_URL}/api/getdevices/`;

    const fetchData = () => {
      fetch(apiUrl, {
        method: "GET",
        credentials: "include",
        headers: new Headers({
          Accept: "application/json",
          "Content-Type": "application/json",
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setTractorData(data.data);
          
          // If a tractor is selected, update popupContent with the latest data
          if (selectedTractorId) {
            const updatedTractor = data.data.find(
              (tractor) => tractor.device_id === selectedTractorId
            );
            if (updatedTractor) {
              setPopupContent(updatedTractor);
              setSprayingData(updatedTractor.historical_data_spray || []);
              setNonSprayingData(updatedTractor.historical_data_nonspray || []);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    };

    fetchData();

    // Set up an interval to fetch data from the API every 5 seconds (changed from 10)
    const intervalId = setInterval(fetchData, 5000);

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, [selectedTractorId]); // Add selectedTractorId as a dependency

  const tractorIcon = (alarm) => {
    const iconUrl = alarm ? RedTracIconUrl : BlueTracIconUrl;

    return L.divIcon({
      className: "custom-marker",
      html: `<img src="${iconUrl}" />`,
      iconSize: [45, 70],
      iconAnchor: [25, 75],
      popupAnchor: [0, 0],
    });
  };

  return (
    <Container fluid>
      <Row>
        <Col xl={4} lg={4} className="Detail-Col">
          <Stack gap={3}>
            <div id="Map-Info" className="text-center">
              <Image src={MapIconUrl} />
              <p>Επιλέξτε στον χάρτη για προβολή λεπτομερειών</p>
            </div>
            {showTable && sprayer && (
              <div className="Sprayer-Info">
                <Table borderless size="sm" className="text-left">
                  <tbody>
                    <tr>
                      <td rowSpan={2}>
                        <Image src={ContactIconUrl} id="Contact-Icon" />
                      </td>
                      <td>
                        {sprayer.first_name} {sprayer.last_name}
                      </td>
                    </tr>
                    <tr>
                      <td>{sprayer.contact_phone}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
            <div>
              {showTable && popupContent && (
                <Table bordered size="sm" responsive id="Tractor-Info">
                  <tbody>
                    <tr>
                      <td>
                        <Image
                          src={
                            popupContent.wind_speed > WIND_SPEED_THRESHOLD ||
                            popupContent.wind_relative_humidity < HUMIDITY_THRESHOLD ||
                            popupContent.temperature_in_celsius > TEMPERATURE_THRESHOLD ||
                            (currentTime - new Date(popupContent.timestamp)) / 60000 > TIME_THRESHOLD ||
                            (popupContent.leak_difference >= LEAK_THRESHOLD)
                              ? errorIconUrl
                              : okIconUrl
                          }
                        />
                      </td>
                      <td>
                        <strong>ID μηχανήματος</strong>
                      </td>
                      <td>{popupContent.device_id}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image
                          src={
                            (currentTime - new Date(popupContent.timestamp)) / 60000 > TIME_THRESHOLD
                              ? errorIconUrl
                              : okIconUrl
                          }
                        />
                      </td>
                      <td>
                        <strong>Χρονοσήμανση</strong>
                      </td>
                      <td>{popupContent.formatted_timestamp || popupContent.timestamp}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image
                          src={
                            popupContent.wind_speed > WIND_SPEED_THRESHOLD
                              ? errorIconUrl
                              : okIconUrl
                          }
                        />
                      </td>
                      <td>
                        <strong>Ταχύτητα Αέρα</strong>
                      </td>
                      <td>{popupContent.wind_speed}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image src={okIconUrl} />
                      </td>
                      <td>
                        <strong>Κατεύθυνση Αέρα</strong>
                      </td>
                      <td>{popupContent.wind_direction_text || popupContent.wind_direction}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image
                          src={
                            popupContent.wind_relative_humidity < HUMIDITY_THRESHOLD
                              ? errorIconUrl
                              : okIconUrl
                          }
                        />
                      </td>
                      <td>
                        <strong>Υγρασία</strong>
                      </td>
                      <td>{popupContent.wind_relative_humidity}</td>
                    </tr>
                    <tr>
                    <td>
                        <Image
                          src={
                            popupContent.temperature_in_celsius > TEMPERATURE_THRESHOLD
                              ? errorIconUrl
                              : okIconUrl
                          }
                        />
                      </td>
                      <td><strong>Θερμοκρασία</strong></td>
                      <td>{popupContent.temperature_in_celsius}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image src={(popupContent.leak_difference >= LEAK_THRESHOLD) ? errorIconUrl : okIconUrl} />
                      </td>
                      <td>
                        <strong>Διαρροή Βυτίου</strong>
                      </td>
                      <td>{(popupContent.leak_difference >= LEAK_THRESHOLD) ? "Ναι" : "Όχι"}</td>
                    </tr>
                    <tr>
                      <td>
                        <Image src={okIconUrl} />
                      </td>
                      <td>
                        <strong>Εφαρμοσμένο μίγμα ψεκασμού</strong>
                      </td>
                      <td>300 ml</td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </div>
          </Stack>
        </Col>
        <Col>
          <MapContainer
            center={[36.8, 22.5]}
            zoom={9}
            className="Map-Container"
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; <a href="https://www.esri.com/en-us/home?srsltid=AfmBOooTThrxEyQ81Eo483JZk8JD--LZtQCBQIjeOWCD3t9Yk-FBK5Xb" target="_blank">ESRI     ....</a> '
            />
            {tractorData.map((tractor) => {
              const isHumidityAlarm = tractor.wind_relative_humidity < HUMIDITY_THRESHOLD;
              const isWindAlarm = tractor.wind_speed > WIND_SPEED_THRESHOLD;
              const isTempAlarm = tractor.temperature_in_celsius > TEMPERATURE_THRESHOLD;
              const leakAlarm = (tractor.leak_difference >= LEAK_THRESHOLD);
              const lastTime = new Date(tractor.timestamp);
              const timeAlarm = (currentTime - lastTime) / 60000 > TIME_THRESHOLD;
              const hasAlarm = isHumidityAlarm || isWindAlarm || isTempAlarm || timeAlarm || leakAlarm;
              const alarmStyle = {
                color: hasAlarm ? "#A46666" : "#2E3B30",
                fontWeight: hasAlarm ? "medium" : "normal",
              };
              const timeAlarmStyle = {
                color: timeAlarm ? "#A46666" : "#2E3B30",
                fontWeight: timeAlarm ? "medium" : "normal",
              };
              const windAlarmStyle = {
                color: isWindAlarm ? "#A46666" : "#2E3B30",
                fontWeight: isWindAlarm ? "medium" : "normal",
              };
              const humidityAlarmStyle = {
                color: isHumidityAlarm ? "#A46666" : "#2E3B30",
                fontWeight: isHumidityAlarm ? "medium" : "normal",
              };
              const tempAlarmStyle = {
                color: isTempAlarm ? "#A46666" : "#2E3B30",
                fontWeight: isTempAlarm ? "medium" : "normal",
              };
              const leakAlarmStyle = {
                color: leakAlarm ? "#A46666" : "#2E3B30",
                fontWeight: leakAlarm ? "medium" : "normal",
              };
              const popupAlarm = hasAlarm ? "RedPopup" : "Popup";

              return (
                <Marker
                  key={tractor.device_id || tractor.id}
                  position={[tractor.latitude, tractor.longitude]}
                  icon={tractorIcon(
                    isWindAlarm || isHumidityAlarm || isTempAlarm || timeAlarm || leakAlarm ? true : false
                  )}
                  style={{ zIndexOffset: 1000 }}
                  className="Marker"
                  eventHandlers={{
                    click: () => handleMarkerClick(tractor),
                  }}
                >
                  <Popup className={popupAlarm}>
                    <Table borderless size="sm" responsive>
                      <tbody>
                        <tr>
                          <td style={alarmStyle}>ID μηχανήματος</td>
                          <td style={alarmStyle}>{tractor.device_id || tractor.id}</td>
                        </tr>
                        <tr>
                          <td style={timeAlarmStyle}>Χρονοσήμανση</td>
                          <td style={timeAlarmStyle}>{tractor.formatted_timestamp || tractor.timestamp}</td>
                        </tr>
                        <tr>
                          <td style={windAlarmStyle}>Ταχύτητα Αέρα</td>
                          <td style={windAlarmStyle}>{tractor.wind_speed}</td>
                        </tr>
                        <tr>
                          <td>Κατεύθυνση Αέρα</td>
                          <td>{tractor.wind_direction_text || tractor.wind_direction}</td>
                        </tr>
                        <tr>
                          <td style={humidityAlarmStyle}>Υγρασία</td>
                          <td style={humidityAlarmStyle}>
                            {tractor.wind_relative_humidity}
                          </td>
                        </tr>
                        <tr>
                          <td style={tempAlarmStyle}>Θερμοκρασία</td>
                          <td style={tempAlarmStyle}>{tractor.temperature_in_celsius}</td>
                        </tr>
                        <tr>
                          <td style={leakAlarmStyle}>Διαρροή Βυτίου</td>
                          <td style={leakAlarmStyle}>{(tractor.leak_difference >= LEAK_THRESHOLD) ? "Ναι" : "Όχι"}</td>
                        </tr>
                        <tr>
                          <td>Εφαρμοσμένο μίγμα ψεκασμού</td>
                          <td>300 ml</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Popup>
                </Marker>
              );
            })}
            {sprayingData && sprayingData.length > 0 && (
              sprayingData.map((data, index) => (
                <CircleMarker
                  key={`spray-${index}`}
                  center={[data.latitude, data.longitude]}
                  radius={2}
                  color="#FF7700"
                  zIndex={1000}
                />
              ))
            )}
            {nonSprayingData && nonSprayingData.length > 0 && (
              <Polyline
                positions={nonSprayingData.map(data => [data.latitude, data.longitude])}
                color="#0066FF"
                weight={3}
                zIndex={500}
              />
            )}
          </MapContainer>
        </Col>
      </Row>
    </Container>
  );
};

export default Map;